import { useQuery } from '@tanstack/react-query'
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'

import { webApi } from '$api'
import useAppStore from '$appStore'

import { CopyButton } from 'src/components/common/display'

/**
 * Web-API path returning the venues the current user can access. Eventaully we
 * should include this additional information in the user-session API response
 * so that we don't have to rely on this old endpoint.
 */
const COMPANIES_PATH = '/r/Companies'

/**
 * A venue as returned by `GET /r/Companies`. Only the fields the venue-info
 * dialog needs are modelled; the endpoint returns more (permissions etc.).
 * See comment above though, in time we want to use a different endpoint so
 * we're not going to try and improve this for now.
 */
interface CompanyInfo {
  id: string
  code: string
  fullName: string
  shortName: string
  ownerFirstName: string
  ownerLastName: string
}

/**
 * A single label/value row. The optional trailing slot hosts the copy button
 * for the company code.
 */
const InfoRow = ({
  label,
  value,
  trailing,
}: {
  label: string
  value: string
  trailing?: React.ReactNode
}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    gap={2}
  >
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
      <Typography
        variant="body2"
        fontWeight={500}
        textAlign="right"
        sx={{ wordBreak: 'break-word' }}
      >
        {value}
      </Typography>
      {trailing}
    </Box>
  </Box>
)

/**
 * Self-contained content showing the active venue's full name, short
 * name, owner name, and company code (with a copy button next to the code).
 *
 * Fetches its own data (`GET /r/Companies`) and resolves the active venue by
 * matching the store's `activeCompany.id`, so it can be used in a self-contained
 * way. Handles loading, error, and venue-not-found states inline.
 */
const VenueInfoContent = () => {
  const activeCompanyId = useAppStore((state) => state.activeCompany?.id)
  const activeCompany = useAppStore((state) => state.activeCompany)

  const {
    data: companies,
    isLoading,
    isError,
  } = useQuery<CompanyInfo[]>({
    queryKey: [COMPANIES_PATH],
    queryFn: () => webApi.get<CompanyInfo[]>(COMPANIES_PATH),
    refetchOnWindowFocus: false,
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" paddingY={3}>
        <CircularProgress />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert severity="error">
        Couldn&apos;t load venue information. Please try again.
      </Alert>
    )
  }

  const venue = companies?.find((company) => company.id === activeCompanyId)

  if (!venue) {
    return <Alert severity="warning">No active venue was found.</Alert>
  }

  const ownerName = `${venue.ownerFirstName ?? ''} ${
    venue.ownerLastName ?? ''
  }`.trim()

  return (
    <Stack spacing={1.5} paddingTop={1} paddingBottom={2}>
      <InfoRow label="Company name" value={venue.fullName} />
      <InfoRow label="Venue name" value={venue.shortName} />
      <InfoRow label="Owner" value={ownerName || '-'} />
      <InfoRow
        label="Company code"
        value={venue.code}
        trailing={
          <CopyButton value={venue.code} ariaLabel="Copy company code" />
        }
      />
    </Stack>
  )
}

export default VenueInfoContent
